import { Geotab, Group } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export type GroupColor = { r: number; g: number; b: number; a: number };

export const DEFAULT_GROUP_COLOR: GroupColor = { r: 255, g: 69, b: 0, a: 255 };

async function addOrUpdateGroup(groupData: {
  geotabClient: Geotab;
  name: string;
  comment: string;
  parentId: string;
  method: string;
  groupId?: string;
  color?: GroupColor;
}) {
  const { geotabClient, name, comment, parentId, method, groupId, color } = groupData;

  const result = await geotabClient.call(method, {
    typeName: "Group",
    entity: {
      name,
      comments: comment,
      parent: { id: parentId },
      // Only for new groups: sending false on Set would reset an existing group's flag.
      ...(method === "Add" && { isGlobalReportingGroup: false }),
      color: color ?? DEFAULT_GROUP_COLOR,
      ...(groupId && { id: groupId }),
    },
  });

  // Add returns the new id, Set returns nothing.
  return result as Group | string | undefined;
}

async function removeGroup({
  geotabClient,
  groupId,
  parentId,
}: {
  geotabClient: Geotab;
  groupId: string;
  parentId: string;
}) {
  await geotabClient.call("Remove", {
    typeName: "Group",
    entity: { id: groupId, parent: { id: parentId } },
  });
}

function useInvalidateGroups() {
  const queryClient = useQueryClient();
  return () =>
    // "group" = single-group lookups (by id/name), "groups" = lists such as useGroups.
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["group"] }),
      queryClient.invalidateQueries({ queryKey: ["groups"] }),
    ]);
}

export function useAddOrUpdateGroup() {
  const invalidateGroups = useInvalidateGroups();

  return useMutation({
    mutationFn: addOrUpdateGroup,
    onSuccess: invalidateGroups,
  });
}

export function useRemoveGroup() {
  const invalidateGroups = useInvalidateGroups();

  return useMutation({
    mutationFn: removeGroup,
    onSuccess: invalidateGroups,
  });
}
